/**
 * Sprite cache backed by IndexedDB.
 * Stores sprite image blobs keyed by URL so they can be reused even if the
 * original file is no longer available (e.g., after a cache clear or external source).
 */

import { DB_STORES } from "../config/constants";
import { dbGet, dbPut } from "./db";
import {
  type SpriteResolution,
  resolvePokemonBackSprite,
  resolvePokemonFrontSprite,
  resolveTrainerSprite,
  testImageExists,
} from "./images";

export type SpriteKind = "native" | "frames" | "unknown";

export type SpriteMetadata = {
  kind: SpriteKind;
};

const metadataCache = new Map<string, SpriteMetadata>();
const blobUrlCache = new Map<string, string>();

/** Fetch a sprite image and store it in IndexedDB as a blob. */
export async function cacheSprite(url: string): Promise<boolean> {
  try {
    const response = await fetch(url);
    if (!response.ok) return false;
    const blob = await response.blob();
    await dbPut(DB_STORES.SPRITES, blob, url);
    return true;
  } catch {
    return false;
  }
}

/** Get a cached sprite blob, or null if not cached. */
export async function getCachedSprite(url: string): Promise<Blob | null> {
  try {
    return (await dbGet<Blob>(DB_STORES.SPRITES, url)) ?? null;
  } catch {
    return null;
  }
}

/** Create an object URL from a cached blob. Caller must revoke when done. */
export function blobToUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}

/** Test whether an image URL can be loaded (file exists). */
export function testImageExistsLocal(url: string): Promise<boolean> {
  return testImageExists(url);
}

function recordMetadata(url: string, kind: SpriteKind): void {
  metadataCache.set(url, { kind });
}

/** Resolve a single native sprite URL, falling back to a cached blob if the original file is missing. */
export async function resolveNativeSpriteUrl(url: string): Promise<string | null> {
  if (await testImageExists(url)) {
    void cacheSprite(url);
    recordMetadata(url, "native");
    return url;
  }

  const cached = await getCachedSprite(url);
  if (cached) {
    const blobUrl = blobToUrl(cached);
    blobUrlCache.set(url, blobUrl);
    recordMetadata(url, "native");
    return blobUrl;
  }

  return null;
}

/** Resolve a sprite resolution to a set of usable URLs, with caching. */
export async function resolveSpriteResolution(
  resolution: SpriteResolution,
): Promise<{ kind: SpriteKind; urls: string[]; intervalMs: number } | null> {
  if (resolution.kind === "native") {
    const resolved = await resolveNativeSpriteUrl(resolution.url);
    if (!resolved) return null;
    recordMetadata(resolution.url, "native");
    return { kind: "native", urls: [resolved], intervalMs: 0 };
  }

  const urls: string[] = [];
  for (const url of resolution.urls) {
    const resolved = await resolveNativeSpriteUrl(url);
    if (!resolved) return null;
    urls.push(resolved);
  }
  recordMetadata(resolution.urls[0], "frames");
  return { kind: "frames", urls, intervalMs: resolution.intervalMs };
}

/** Resolve a sprite URL, preferring a cached blob if the original file is missing. */
export async function resolveSpriteUrl(url: string): Promise<string | null> {
  return resolveNativeSpriteUrl(url);
}

/** Determine whether a resolved URL is an animated sprite. */
export function isAnimatedSprite(url: string): boolean {
  const ext = url.split(".").pop()?.toLowerCase();
  return ext === "gif" || ext === "apng" || ext === "png";
}

/** Determine whether a sprite resolution is animated. */
export function isAnimatedResolution(resolution: SpriteResolution): boolean {
  if (resolution.kind === "frames") return true;
  const ext = resolution.url.split(".").pop()?.toLowerCase();
  return ext === "gif" || ext === "apng" || ext === "png";
}

/** Preload a list of sprite URLs into the cache. */
export async function preloadSprites(urls: string[]): Promise<void> {
  await Promise.all(urls.map((url) => resolveSpriteUrl(url)));
}

/** Convenience: resolve a Pokemon front sprite with caching and format detection. */
export async function resolvePokemonFrontUrl(
  species: string,
  shiny = false,
  preferAnimated = true,
): Promise<string | null> {
  const resolution = await resolvePokemonFrontSprite(species, shiny, preferAnimated);
  if (!resolution) return null;
  const result = await resolveSpriteResolution(resolution);
  if (!result) return null;
  return result.urls[0] ?? null;
}

/** Convenience: resolve a Pokemon back sprite with caching and format detection. */
export async function resolvePokemonBackUrl(
  species: string,
  shiny = false,
  preferAnimated = true,
): Promise<string | null> {
  const resolution = await resolvePokemonBackSprite(species, shiny, preferAnimated);
  if (!resolution) return null;
  const result = await resolveSpriteResolution(resolution);
  if (!result) return null;
  return result.urls[0] ?? null;
}

/** Convenience: resolve a trainer sprite with caching and format detection. */
export async function resolveTrainerUrl(
  kind: "front" | "back" | "vs",
  id: string,
  preferAnimated = true,
): Promise<string | null> {
  const resolution = await resolveTrainerSprite(kind, id, preferAnimated);
  if (!resolution) return null;
  const result = await resolveSpriteResolution(resolution);
  if (!result) return null;
  return result.urls[0] ?? null;
}

/** Revoke all blob URLs created from cached sprites. */
export function revokeCachedBlobUrls(): void {
  for (const blobUrl of blobUrlCache.values()) {
    URL.revokeObjectURL(blobUrl);
  }
  blobUrlCache.clear();
}
